import {
  QueriesOptions,
  QueryClient,
  QueryKey,
  SuspenseQueriesOptions,
  useQueries,
  useSuspenseQueries,
} from "@tanstack/react-query";
import { useMemo } from "react";

type ExtractData<T extends Array<{ queryFn?: (...args: any[]) => any }>> = {
  [K in keyof T]: Awaited<
    ReturnType<Exclude<T[K]["queryFn"], null | undefined>>
  >;
};

interface CreateQueryProps<T extends Array<any>, K> {
  queries: readonly [...QueriesOptions<T>];
  queryFn(...result: ExtractData<T>): K;
}

export function createQuery<T extends Array<any>, K>({
  queries,
  queryFn,
}: CreateQueryProps<T, K>) {
  return {
    queries,
    useQuery: () => {
      const results = useQueries({
        queries: queries as readonly [...QueriesOptions<T>],
      });

      const isPending = results.some((result) => result.isPending);
      const data = results.map((result) => result.data);

      // Since data returns an array of the same length each time
      // we can memoize the queryFn to prevent unnecessary recalculations
      const combinedData = useMemo(() => {
        if (data.includes(undefined)) {
          return undefined;
        }

        return queryFn(...(data as ExtractData<T>));
        // biome-ignore lint/correctness/useExhaustiveDependencies: Dynamic dependencies array
      }, data);

      return {
        isLoading: results.some((result) => result.isLoading),
        isPending,
        isError: results.some((result) => result.isError),
        data: combinedData,
      };
    },
    useSuspenseQuery: () => {
      const queryInfo = useSuspenseQueries({
        queries: queries as unknown as readonly [...SuspenseQueriesOptions<T>],
      });

      const data = queryInfo.map((info) => info.data);
      const combinedData = useMemo(() => {
        return queryFn(...(data as ExtractData<T>));
        // biome-ignore lint/correctness/useExhaustiveDependencies: Dynamic dependencies array
      }, data);

      return {
        data: combinedData,
      };
    },
    ensureQuery: async (queryClient: QueryClient) => {
      const data = await Promise.all(
        queries.map((query) => queryClient.ensureQueryData(query)),
      );
      return queryFn(...(data as ExtractData<T>));
    },
    prefetchQuery: async (queryClient: QueryClient) => {
      return Promise.all(
        queries.map((query) => queryClient.prefetchQuery(query)),
      );
    },
    invalidate: (queryClient: QueryClient) => {
      return Promise.all(
        queries.map((query) => queryClient.invalidateQueries(query)),
      );
    },
  };
}

export const enhanceQueryKey = <
  TQuery extends { queryKey: TQueryKey },
  TQueryKey extends QueryKey = QueryKey,
>(
  query: TQuery,
  ...append: any[]
): TQuery => ({
  ...query,
  queryKey: [...query.queryKey, ...append],
});
